/**
 * 💜 HEARTBEATS CONTEXT - Architecture Mozart/Jobs/Tesla
 * 
 * Provider principal pour le module Heartbeats.
 * Gère l'état global, le profil utilisateur, et la navigation.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { useHeartbeatsStore } from '../../stores/heartbeatsStore';

const HeartbeatsContext = createContext(null);

export const HeartbeatsProvider = ({ children, customerId, customerData, isDarkMode }) => {
  // ============================================
  // ÉTAT LOCAL
  // ============================================
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('discover');
  const [isConfidentialMode, setIsConfidentialMode] = useState(false);
  
  // Profil Heartbeats
  const [profile, setProfile] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState(0);
  
  // Guide utilisateur
  const [guide, setGuide] = useState(null);
  const [showGuideWelcome, setShowGuideWelcome] = useState(false);
  
  // Modals globaux
  const [showAvatarCreator, setShowAvatarCreator] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showCrownShop, setShowCrownShop] = useState(false);
  const [showGiftShop, setShowGiftShop] = useState(false);
  
  // Zustand store
  const store = useHeartbeatsStore();

  // ============================================
  // CHARGEMENT INITIAL
  // ============================================
  const loadProfile = useCallback(async () => {
    if (!customerId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.getHeartbeatsProfile(customerId);
      if (response.data) {
        setProfile(response.data);
        store.setProfile(response.data);
        
        // Calculer la complétion du profil
        const completion = calculateProfileCompletion(response.data);
        setProfileCompletion(completion);
        
        // Mode confidentiel
        const savedMode = localStorage.getItem(`heartbeats_confidential_${customerId}`);
        setIsConfidentialMode(savedMode === 'true');
      }
    } catch (err) {
      // Profil n'existe pas encore
      if (err.response?.status === 404) {
        setProfile(null);
      } else {
        setError('Erreur de chargement du profil');
        console.error('HeartbeatsContext loadProfile error:', err);
      }
    } finally {
      setIsLoading(false);
    }
  }, [customerId, store]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // ============================================
  // ACTIONS
  // ============================================
  const updateProfile = useCallback(async (updates) => {
    if (!customerId || !profile) return { success: false };
    
    try {
      const response = await api.updateHeartbeatsProfile(customerId, updates);
      if (response.data) {
        setProfile(prev => ({ ...prev, ...updates }));
        store.setProfile({ ...profile, ...updates });
        return { success: true };
      }
    } catch (err) {
      console.error('HeartbeatsContext updateProfile error:', err);
      return { success: false, error: err.message };
    }
  }, [customerId, profile, store]);

  const toggleConfidentialMode = useCallback(() => {
    const newMode = !isConfidentialMode;
    setIsConfidentialMode(newMode);
    localStorage.setItem(`heartbeats_confidential_${customerId}`, newMode.toString());
    return newMode;
  }, [customerId, isConfidentialMode]);

  const refreshProfile = useCallback(() => {
    return loadProfile();
  }, [loadProfile]);

  // ============================================
  // VALEUR DU CONTEXTE
  // ============================================
  const value = {
    // État
    isLoading,
    error,
    isDarkMode,
    customerId,
    customerData,
    
    // Profil
    profile,
    profileCompletion,
    
    // Navigation
    activeTab,
    setActiveTab,
    
    // Mode confidentiel
    isConfidentialMode,
    toggleConfidentialMode,
    
    // Guide
    guide,
    setGuide,
    showGuideWelcome,
    setShowGuideWelcome,
    
    // Modals
    showAvatarCreator,
    setShowAvatarCreator,
    showProfileEdit,
    setShowProfileEdit,
    showCrownShop,
    setShowCrownShop,
    showGiftShop,
    setShowGiftShop,
    
    // Actions
    updateProfile,
    refreshProfile,
    
    // Store Zustand
    store
  };

  return (
    <HeartbeatsContext.Provider value={value}>
      {children}
    </HeartbeatsContext.Provider>
  );
};

// ============================================
// HOOK D'UTILISATION
// ============================================
export const useHeartbeats = () => {
  const context = useContext(HeartbeatsContext);
  if (!context) {
    throw new Error('useHeartbeats must be used within HeartbeatsProvider');
  }
  return context;
};

// ============================================
// UTILITAIRES
// ============================================
function calculateProfileCompletion(profile) {
  if (!profile) return 0;
  
  let score = 0;
  const weights = {
    displayName: 10,
    bio: 15,
    avatarImage: 20,
    photos: 15,
    passions: 20,
    soulAnswers: 10,
    aspirations: 10
  };
  
  if (profile.displayName) score += weights.displayName;
  if (profile.bio && profile.bio.length > 10) score += weights.bio;
  if (profile.avatarImage) score += weights.avatarImage;
  if (profile.photos?.length > 0) score += weights.photos;
  if (Object.keys(profile.passions || {}).length > 0) score += weights.passions;
  if (Object.keys(profile.soulAnswers || {}).length > 0) score += weights.soulAnswers;
  if (profile.aspirations?.dreams?.length > 0) score += weights.aspirations;
  
  return score;
}

export default HeartbeatsContext;
